import { Options, Vue } from 'vue-class-component';
import 'lazysizes';

@Options({
	emits: ['pictureLoaded'],
	props: {
		img: {
			type: Object,
			required: true,
		},
		alt: {
			type: String,
			required: false,
		},
		dpr: {
			type: Number,
			required: false,
			default: window.devicePixelRatio,
		},
		fallback: {
			type: Boolean,
			required: false,
			default: true,
		},
		thumb: {
			type: Boolean,
			required: false,
			default: false,
		},
	},
	watch: {
		img() {
			this.calculateSrc();
		},
	},
})
class ResponseImg extends Vue {
	failed: boolean = false;

	img!: {
		'1x'?: string;
		'2x'?: string;
		'1x_webp'?: string;
		'2x_webp'?: string;
		lqip?: string;
		mobile?: string;
		mobile_webp?: string;
		thumb?: string;
		thumb_webp?: string;
		aspect_ratio?: string;
	};

	alt!: string;
	dpr!: number;
	thumb!: boolean;
	fallback!: boolean;

	emptyGif: string = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

	src: string = '';
	dataSrc: string = '';
	dataSrcsetWebp: string = '';

	calculateTimeout: any;
	windowInnerWidth = window.innerWidth;
	isPrerender: boolean = window.navigator.userAgent.indexOf('HeadlessChrome') !== -1;

	created() {
		this.src = this.emptyGif;

		this.calculateSrc();
		window.addEventListener('resize', this.handleResize);
	}

	destroyed() {
		window.removeEventListener('resize', this.handleResize);
	}

	onPictureLoaded() {
		this.$emit('pictureLoaded');
	}

	onPictureError() {
		if (this.src && this.fallback && !this.isPrerender) {
			this.failed = true;
			this.src = this.emptyGif;
			this.dataSrc = '';
			this.dataSrcsetWebp = '';
		}
	}

	get style(): {[key: string]: string} {
		const style: {[key: string]: string} = {};

		if (this.img.aspect_ratio) {
			style.aspectRatio = this.img.aspect_ratio;
		}

		return style;
	}

	private calculateSrc(): void {
		this.failed = false;

		this.removeClasses();

		const isDesktop = window.innerWidth >= 768;

		if (this.isPrerender) {
			this.src = isDesktop ? this.getDesktopSrc() as string : this.getMobileSrc() as string;
		} else {
			if (this.img.lqip) {
				this.src = this.img.lqip;
			}

			this.dataSrc = isDesktop ? this.getDesktopSrc() as string : this.getMobileSrc() as string;
		}

		this.dataSrcsetWebp = isDesktop ? this.getDesktopWebpSrcset() as string : this.getMobileWebpSrcset() as string;

		if (!this.isPrerender) {
			setTimeout(() => this.addClasses());
		}
	}

	private handleResize(): void {
		if (window.innerWidth === this.windowInnerWidth) {
			return;
		}

		this.windowInnerWidth = window.innerWidth;

		clearTimeout(this.calculateTimeout);
		this.calculateTimeout = setTimeout(this.calculateSrc, 50);
	}

	private getDesktopSrc(): string | undefined {
		if (this.thumb && this.img.thumb) {
			return this.img.thumb;
		}

		if (this.dpr > 1 && this.img['2x']) {
			return this.img['2x'];
		}

		return this.img['1x'];
	}

	private getMobileSrc(): string | undefined {
		if (this.thumb && this.img.thumb) {
			return this.img.thumb;
		}

		return this.img.mobile || this.getDesktopSrc();
	}

	private getDesktopWebpSrcset(): string {
		const srcset = [];

		if (this.thumb && this.img.thumb_webp) {
			srcset.push(this.img.thumb_webp);
		} else {
			if (this.dpr > 1 && this.img['2x_webp']) {
				srcset.push(this.img['2x_webp'] + ' 2x');
			}

			if (this.img['1x_webp']) {
				srcset.push(this.img['1x_webp'] + ' 1x');
			}
		}

		return srcset.join(', ');
	}

	private getMobileWebpSrcset(): string | undefined {
		if (this.thumb && this.img.thumb_webp) {
			return this.img.thumb_webp;
		}

		if (this.img.mobile_webp) {
			return this.img.mobile_webp;
		}

		if (!this.img.mobile) {
			return this.getDesktopWebpSrcset();
		}
	}

	private removeClasses(): void {
		const imgElement: HTMLElement = this.$refs.img as HTMLElement;

		if (imgElement) {
			imgElement.classList.remove('blur');
			imgElement.classList.remove('lazyload');
			imgElement.classList.remove('lazyloading');
			imgElement.classList.remove('lazyloaded');
		}
	}

	private addClasses(): void {
		const imgElement: HTMLElement = this.$refs.img as HTMLElement;

		if (imgElement) {
			if (this.img.lqip) {
				imgElement.classList.add('blur');
			}

			imgElement.classList.add('lazyload');
			imgElement.classList.add('lazyloading');
		}
	}
}


export default ResponseImg;
